/**
 * Spec 294 (decisión del dueño, 7-oct): salir de Reglas con cambios propios sin
 * aplicar pregunta antes. Una sola «guardia» a la vez (la pone Reglas mientras
 * haya cambios de esa persona) y cuatro caminos que pasan por ella:
 *
 * - las pestañas de Estrategias y los botones que navegan a mano (`pedirSalida`);
 * - el MENÚ LATERAL y cualquier enlace interno: el router es `BrowserRouter`
 *   (sin `useBlocker`), así que se frena el clic en un `<a>` antes de que lo vea
 *   React (escucha en `document`, en la fase de captura) y, si la persona acepta,
 *   se repite el mismo clic dejándolo pasar;
 * - cerrar o recargar la ventana: `beforeunload` (el aviso del navegador) y, si
 *   se va igual, `alCerrar` al `pagehide` (descartar sin esperar respuesta);
 * - la flecha ATRÁS del navegador (ronda de la T11): al poner la guardia se deja
 *   una «marca» en la historia (una entrada más con la misma dirección); la
 *   flecha atrás sale de la marca a la entrada de Reglas (misma dirección: la
 *   página sigue), se vuelve a poner la marca y se pregunta; si la persona
 *   acepta, se vuelve atrás de verdad (dos pasos: la marca y Reglas).
 *
 * No cubre un `navigate()` que no pase por `pedirSalida` (anotado en el registro).
 * Un enlace a la MISMA ruta (otra búsqueda, p. ej. «Estrategias de Mercado»
 * estando en `?destino=reglas`) no es salir: la página no se vuelve a montar.
 */

export interface GuardiaDeSalida {
  /** Muestra la pregunta; si la persona acepta (y se descartó), llama a `salir`. */
  preguntar: (salir: () => void) => void;
  /** Al irse de la página después del aviso del navegador. */
  alCerrar?: () => void;
  /**
   * La flecha atrás compara la RUTA (`pathname`) y no la dirección completa. Para una pantalla
   * que cambia `?paso=` / `?item=` con `replace` (el editor del Pricer): ahí la dirección de la
   * entrada de antes difiere de la de la marca aunque la página sea la misma. Las demás
   * pantallas no lo piden y siguen comparando la dirección completa, como siempre.
   */
  ignorarBusqueda?: boolean;
}

let guardia: GuardiaDeSalida | null = null;
/** Mientras se repite un clic ya aceptado, el clic pasa. */
let dejarPasar = false;

/** La marca de la flecha atrás: va en el `state` de la entrada que se agrega. */
const MARCA = '__salidaConCambios';
/** La dirección donde se puso la marca: una vuelta atrás a otra dirección no se frena. */
let direccionDeLaMarca: string | null = null;
/** La ruta donde se puso la marca (para las guardias con `ignorarBusqueda`). */
let rutaDeLaMarca: string | null = null;
/** Los `popstate` de las vueltas que pide este módulo (quitar la marca): no preguntan. */
const vueltasPropias = new WeakSet<Event>();

function enLaMarca(): boolean {
  const s: unknown = window.history.state;
  return typeof s === 'object' && s !== null && (s as Record<string, unknown>)[MARCA] === true;
}

function ponerMarca() {
  try {
    if (enLaMarca()) return;
    const s: unknown = window.history.state;
    // Se copia el `state` del router (su `key` y su `idx`) para no confundirlo.
    const base = typeof s === 'object' && s !== null ? (s as Record<string, unknown>) : {};
    window.history.pushState({ ...base, [MARCA]: true }, '', window.location.href);
    direccionDeLaMarca = window.location.href;
    rutaDeLaMarca = window.location.pathname;
  } catch {
    /* sin historia (o bloqueada): queda sin la flecha atrás */
  }
}

/**
 * Un `replace` del router (cambiar `?paso=`, o pasar de `/new` a `/:id` al guardar) pisa el
 * `state` de la entrada y con él la marca. Esto la vuelve a poner en ESA entrada, sin sumar
 * otra, y anota la dirección nueva. Sin guardia no hace nada.
 */
export function renovarMarca(): void {
  if (!guardia) return;
  try {
    const s: unknown = window.history.state;
    const base = typeof s === 'object' && s !== null ? (s as Record<string, unknown>) : {};
    if (!enLaMarca())
      window.history.replaceState({ ...base, [MARCA]: true }, '', window.location.href);
    direccionDeLaMarca = window.location.href;
    rutaDeLaMarca = window.location.pathname;
  } catch {
    /* sin historia: queda como estaba */
  }
}

/**
 * Resuelve cuando la marca ya no está en la historia: después de quitar la guardia, la vuelta
 * atrás que la saca es asíncrona, y un `navigate(…, { replace: true })` hecho antes se cruza con
 * ella y deja la entrada vieja (B-1: guardar una cotización nueva dejaba `/new` detrás).
 */
export function esperarSinMarca(): Promise<void> {
  return new Promise((resolver) => {
    const hayQueEsperar = () => guardia === null && enLaMarca();
    if (guardia === null && !enLaMarca()) {
      // La baja de la marca se agenda con `setTimeout(0)`: se deja pasar ese turno.
      setTimeout(() => (hayQueEsperar() ? esperar() : resolver()), 0);
      return;
    }
    esperar();
    function esperar() {
      const fin = () => {
        window.removeEventListener('popstate', fin);
        clearTimeout(porLasDudas);
        resolver();
      };
      window.addEventListener('popstate', fin);
      const porLasDudas = setTimeout(fin, 700);
    }
  });
}

/**
 * Vuelve de la marca a la entrada de Reglas (la misma dirección) y DESPUÉS sigue:
 * así salir deja la historia como si la marca no hubiera estado (si no, quedaba
 * una flecha atrás «muerta», a la misma dirección). Si no se está en la marca,
 * sigue de una. Si el navegador no avisa la vuelta, sigue igual al rato.
 */
function salirDeLaMarca(seguir: () => void = () => {}) {
  if (!enLaMarca()) {
    seguir();
    return;
  }
  let listo = false;
  const terminar = () => {
    if (listo) return;
    listo = true;
    clearTimeout(porLasDudas);
    seguir();
  };
  // Se escucha antes que la guardia que se ponga después: la marca su `popstate`.
  const alLlegar = (e: Event) => {
    vueltasPropias.add(e);
    window.removeEventListener('popstate', alLlegar);
    clearTimeout(olvidar);
    terminar();
  };
  window.addEventListener('popstate', alLlegar);
  // Si el navegador tarda en avisar la vuelta, se sigue igual al rato. Pero la
  // escucha NO se va con ese rato: su `popstate`, cuando llegue, sigue siendo
  // nuestro. Antes se iba a los 500 ms y un aviso tardío (navegador lento,
  // máquina cargada) lo tomaba la guardia siguiente como la flecha atrás y
  // preguntaba «Tenés N cambios sin aplicar» sin que nadie quisiera salir.
  const porLasDudas = setTimeout(terminar, 500);
  const olvidar = setTimeout(() => window.removeEventListener('popstate', alLlegar), 10_000);
  window.history.back();
}

/**
 * Se quitó la guardia sin salir (se aplicó o se descartó): la marca se va.
 * Después de este turno: si en el mismo turno se puso otra guardia (Reglas la
 * reemplaza cuando cambia la cuenta), la marca sigue sirviendo.
 */
function quitarMarcaSiQuedo() {
  setTimeout(() => {
    if (!guardia) salirDeLaMarca();
  }, 0);
}

function alVolverAtras(e: PopStateEvent) {
  if (vueltasPropias.has(e)) return;
  const g = guardia;
  if (!g) return;
  // Hacia adelante, a la marca: no es salir.
  const s: unknown = e.state;
  if (typeof s === 'object' && s !== null && (s as Record<string, unknown>)[MARCA] === true) return;
  // A otra dirección: la página ya se está yendo (lo cubre «de antes» al volver).
  const esLaMismaPagina = g.ignorarBusqueda
    ? window.location.pathname === rutaDeLaMarca
    : window.location.href === direccionDeLaMarca;
  if (!esLaMismaPagina) return;
  // De la marca a la entrada de Reglas: la página sigue. Se vuelve a poner la
  // marca (otra flecha atrás vuelve a preguntar) y se pregunta.
  ponerMarca();
  g.preguntar(() => {
    if (guardia === g) {
      guardia = null;
      desinstalar(false);
    }
    window.history.go(-2);
  });
}

function alHacerClic(e: MouseEvent) {
  if (!guardia || dejarPasar) return;
  if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
  const destino = e.target instanceof Element ? e.target.closest('a[href]') : null;
  if (!(destino instanceof HTMLAnchorElement)) return;
  if (destino.target && destino.target !== '_self') return;
  if (destino.hasAttribute('download')) return;
  let url: URL;
  try {
    url = new URL(destino.href, window.location.href);
  } catch {
    return;
  }
  // Un enlace a otro sitio abre afuera: lo cubre `beforeunload` si es en esta pestaña.
  if (url.origin !== window.location.origin) return;
  // Un enlace a la misma ruta (un ancla u otra búsqueda) no es salir: la página no
  // se vuelve a montar (ronda de la T11, I2: descartar y quedarse perdía los cambios).
  if (url.pathname === window.location.pathname) return;
  e.preventDefault();
  e.stopPropagation();
  pedirSalida(() => {
    dejarPasar = true;
    try {
      if (destino.isConnected) destino.click();
      else window.location.assign(url.href);
    } finally {
      dejarPasar = false;
    }
  });
}

function alQuererCerrar(e: BeforeUnloadEvent) {
  if (!guardia) return;
  e.preventDefault();
  // Los navegadores viejos piden `returnValue` para mostrar su aviso.
  e.returnValue = '';
}

function alIrse() {
  guardia?.alCerrar?.();
}

function instalar() {
  document.addEventListener('click', alHacerClic, true);
  window.addEventListener('beforeunload', alQuererCerrar);
  window.addEventListener('pagehide', alIrse);
  window.addEventListener('popstate', alVolverAtras);
  ponerMarca();
}

/** `quitarMarca`: si no se sale (o se sale sin cambiar de entrada), la marca se va. */
function desinstalar(quitarMarca = true) {
  document.removeEventListener('click', alHacerClic, true);
  window.removeEventListener('beforeunload', alQuererCerrar);
  window.removeEventListener('pagehide', alIrse);
  window.removeEventListener('popstate', alVolverAtras);
  if (quitarMarca) quitarMarcaSiQuedo();
}

/** Pone la guardia (reemplaza la que hubiera). Devuelve cómo quitarla. */
export function ponerGuardiaDeSalida(g: GuardiaDeSalida): () => void {
  if (!guardia) instalar();
  guardia = g;
  return () => {
    if (guardia !== g) return;
    guardia = null;
    desinstalar();
  };
}

export function hayGuardiaDeSalida(): boolean {
  return guardia != null;
}

/**
 * Salir (cambiar de pestaña, navegar, cambiar de empresa): sin guardia, sigue; con
 * guardia, pregunta y sigue sólo si la persona acepta. Al aceptar, la guardia se
 * quita ANTES de seguir: así la recarga o el enlace que siguen no vuelven a avisar.
 * Y se sigue después de quitar la marca de la flecha atrás (un instante después).
 */
export function pedirSalida(continuar: () => void): void {
  const g = guardia;
  if (!g) {
    continuar();
    return;
  }
  g.preguntar(() => {
    if (guardia === g) {
      guardia = null;
      desinstalar(false);
    }
    // Primero se quita la marca de la flecha atrás, después se sale.
    salirDeLaMarca(continuar);
  });
}
